import { InputBase, Popper, Typography, ClickAwayListener } from '@mui/material'
import { styled, keyframes } from '@mui/system'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { colorPalette } from '../../../../../style/colorPalette'
import { Checkbox } from '../../../../../components/inputs/Checkbox'
import { SelectOption } from '../../../../../model/selectOption'
import { intl } from '../../../../../../i18n'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import {
  buildTree,
  countTreeNodes,
  filterTree,
  TreeSelectOption,
} from './treeUtils'

// sticky top 計算用の行高さ（padding除去後）
const ROW_HEIGHT = 26

type Props = {
  open: boolean
  anchorEl: Element | undefined
  options: SelectOption[]
  parentMap: Record<string, string>
  isLoading?: boolean
  selectedValues: Set<string>
  onToggleItem: (value: string) => void
  onClose: () => void
}

const ProcessScopeSearchSelectPopper = ({
  open,
  anchorEl,
  options,
  parentMap,
  isLoading,
  selectedValues,
  onToggleItem,
  onClose,
}: Props) => {
  const [ready, setReady] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    const timerId = setTimeout(() => setReady(true), 0)
    return () => {
      clearTimeout(timerId)
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  useEffect(() => {
    setExpandedNodes(new Set(options.map(o => o.value)))
  }, [options])

  const handleSearchTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => {
        setFilterText(value)
        setExpandedNodes(new Set(options.map(o => o.value)))
      }, 300)
    },
    [options]
  )

  const toggleExpand = useCallback((e: React.MouseEvent, value: string) => {
    e.stopPropagation()
    setExpandedNodes(prev => {
      const next = new Set(prev)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      return next
    })
  }, [])

  const tree = useMemo(
    () => buildTree(options, parentMap),
    [options, parentMap]
  )

  const filteredTree = useMemo(
    () => filterTree(tree, filterText),
    [tree, filterText]
  )

  const filteredCount = useMemo(
    () => countTreeNodes(filteredTree),
    [filteredTree]
  )

  const totalCount = useMemo(() => countTreeNodes(tree), [tree])

  const renderNode = (node: TreeSelectOption, depth: number) => {
    const hasChildren = node.children.length > 0
    const isExpanded = expandedNodes.has(node.value)
    const stickyTop = depth * ROW_HEIGHT

    return (
      <div key={node.value}>
        <OptionItem
          onClick={() => onToggleItem(node.value)}
          style={{
            paddingLeft: `${depth * 20 + 2}px`,
            ...(hasChildren && {
              position: 'sticky',
              top: `${stickyTop}px`,
              zIndex: 100 - depth,
              background: colorPalette.monotone[0],
              paddingTop: 0,
              paddingBottom: 0,
            }),
          }}
        >
          {hasChildren ? (
            <ExpandToggle onClick={e => toggleExpand(e, node.value)}>
              {isExpanded ? (
                <ExpandLessRounded
                  sx={{
                    fontSize: 16,
                    color: colorPalette.monotone[4],
                    transform: 'rotate(180deg)',
                  }}
                />
              ) : (
                <ExpandMoreRounded
                  sx={{
                    fontSize: 16,
                    color: colorPalette.monotone[4],
                    transform: 'rotate(-90deg)',
                  }}
                />
              )}
            </ExpandToggle>
          ) : (
            <ExpandTogglePlaceholder />
          )}
          <Checkbox
            sx={{ marginRight: '8px' }}
            checked={selectedValues.has(node.value)}
          />
          {node.iconUrl && <img src={node.iconUrl} />}
          <OptionLabel>
            {depth === 0 && node.tooltip && (
              <OptionPath>{node.tooltip}</OptionPath>
            )}
            <Typography sx={{ color: colorPalette.monotone[4] }}>
              {node.name}
            </Typography>
          </OptionLabel>
        </OptionItem>
        {hasChildren &&
          isExpanded &&
          node.children.map(child => renderNode(child, depth + 1))}
      </div>
    )
  }

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      sx={{
        zIndex: 1301,
        background: colorPalette.monotone[0],
        boxShadow:
          'rgb(0 0 0 / 20%) 0px 5px 5px -3px, rgb(0 0 0 / 14%) 0px 8px 10px 1px, rgb(0 0 0 / 12%) 0px 3px 14px 2px',
        borderRadius: '4px',
        padding: '10px',
        minWidth: '445px',
        maxWidth: '700px',
      }}
      placement="bottom-start"
      modifiers={[
        {
          name: 'offset',
          options: { offset: [0, 20] },
        },
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
      ]}
      disablePortal={true}
    >
      <ClickAwayListener onClickAway={onClose}>
        <PopperContent>
          <InputBase
            autoFocus={true}
            placeholder={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.processScope.searchPlaceholder',
            })}
            onChange={handleSearchTextChange}
            sx={{
              border: `1px solid ${colorPalette.monotone[2]}`,
              borderRadius: '4px',
              padding: '4px 8px',
              marginBottom: '8px',
              fontSize: '14px',
            }}
          />
          <OptionsList>
            {!ready || isLoading ? (
              <LoadingContainer>
                <Spinner />
              </LoadingContainer>
            ) : (
              filteredTree.map(root => renderNode(root, 0))
            )}
          </OptionsList>
          <FooterContainer>
            <CountLabel>
              <CountNumerator>
                {filteredCount}
                {intl.formatMessage({
                  id: 'wbsItemSearchFilterPanel.processScope.count',
                })}
              </CountNumerator>
              {' / '}
              {totalCount}
              {intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.processScope.count',
              })}
            </CountLabel>
          </FooterContainer>
        </PopperContent>
      </ClickAwayListener>
    </Popper>
  )
}

const PopperContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

const OptionsList = styled('div')({
  maxHeight: '600px',
  overflowY: 'auto',
})

const OptionItem = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  cursor: 'pointer',
  '&:hover': {
    background: colorPalette.monotone[1],
  },
  borderRadius: '4px',
  padding: '2px 0',
})

const ExpandToggle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  cursor: 'pointer',
  flexShrink: 0,
})

const OptionLabel = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  margin: '2px 10px',
})

const OptionPath = styled('span')({
  fontSize: '11px',
  lineHeight: '14px',
  color: colorPalette.monotone[3],
})

const ExpandTogglePlaceholder = styled('div')({
  width: '20px',
  height: '20px',
  flexShrink: 0,
})

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px 0',
})

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const Spinner = styled('div')({
  width: '28px',
  height: '28px',
  border: `3px solid ${colorPalette.monotone[2]}`,
  borderTop: `3px solid ${colorPalette.skyBlue[7]}`,
  borderRadius: '50%',
  animation: `${spin} 0.8s linear infinite`,
})

const FooterContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  marginTop: '8px',
})

const CountLabel = styled('span')({
  fontSize: '12px',
  color: colorPalette.monotone[3],
})

const CountNumerator = styled('span')({
  fontWeight: 'bold',
  color: colorPalette.monotone[5],
})

export default ProcessScopeSearchSelectPopper
